import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { EditableTableColumn } from '../../../models/editable-table-column.model';
import { getDisplayText, isRequiredEmpty } from '../editable-table-cell.util';

// One table row, as its own OnPush component — split out of EditableTableComponent
// so that editing a cell only re-checks THIS row, not all of them. See
// PARTE-2-OTIMIZACAO-TABELA-review-calls.md for the full "why".
//
// Selector matches the <tr> itself (no wrapping element), so the rendered HTML
// stays a plain, valid <table>.
@Component({
  selector: 'tr[app-editable-table-row]',
  templateUrl: './editable-table-row.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'transition-all duration-200 h-14 hover:brightness-95' },
})
export class EditableTableRowComponent<T = any> {
  @Input({ required: true }) row!: T;
  @Input() columns: EditableTableColumn<T>[] = [];
  @Input() selectedColumns: EditableTableColumn<T>[] = [];
  @Input() emptyOptions: any[] = [];
  @Input() rowIndex = 0;
  @Input() showRowNumber = false;
  @Input() showToggleColumn = false;
  @Input() toggleColumnAfterIndex: number | null = null;
  @Input() loadingFields: string[] = [];

  get rowNumberDisplay(): string {
    return String(this.rowIndex + 1).padStart(2, '0');
  }
  @Input() isSelected = false;
  @Input() isAllSelected = false;
  @Input() prevRowSelected = false;
  @Input() nextRowSelected = false;
  @Input() isLastRow = false;

  @Output() cellValueChanged = new EventEmitter<{ col: EditableTableColumn<T>; value: any }>();

  getDisplayText(col: EditableTableColumn<T>): string {
    return getDisplayText(this.row, col);
  }

  isRequiredEmpty(col: EditableTableColumn<T>): boolean {
    return isRequiredEmpty(this.row, col);
  }

  isColumnSelected(col: EditableTableColumn<T>): boolean {
    return this.selectedColumns.includes(col);
  }

  isDisabled(col: EditableTableColumn<T>): boolean {
    return col.disabled ? col.disabled(this.row, this.rowIndex) : false;
  }

  isFieldLoading(col: EditableTableColumn<T>): boolean {
    return this.loadingFields.includes(col.field);
  }

  // Same "cap only the ends of a contiguous run" logic as before — just fed by
  // inputs (prev/next selected) instead of reaching into a shared rows array.
  // Bound to the host (the <tr> itself, since this component's selector IS the
  // <tr>), matching where this shadow used to be drawn.
  @HostBinding('style.boxShadow')
  get rowBorderShadowHost(): string | null {
    return this.rowBorderShadow();
  }

  private rowBorderShadow(): string | null {
    if (this.isAllSelected || !this.isSelected) return null;

    const parts = ['inset 2px 0 0 0 var(--primary-500)', 'inset -2px 0 0 0 var(--primary-500)'];
    if (!this.prevRowSelected) parts.push('inset 0 2px 0 0 var(--primary-500)');
    if (!this.nextRowSelected) parts.push('inset 0 -2px 0 0 var(--primary-500)');
    return parts.join(', ');
  }

  columnBorderShadow(col: EditableTableColumn<T>): string | null {
    if (this.isAllSelected || !this.isColumnSelected(col)) return null;

    const index = this.columns.indexOf(col);
    const prevSelected = index > 0 && this.isColumnSelected(this.columns[index - 1]);
    const nextSelected =
      index < this.columns.length - 1 && this.isColumnSelected(this.columns[index + 1]);

    const parts: string[] = [];
    if (!prevSelected) parts.push('inset 2px 0 0 0 var(--primary-500)');
    if (!nextSelected) parts.push('inset -2px 0 0 0 var(--primary-500)');
    if (this.isLastRow) parts.push('inset 0 -2px 0 0 var(--primary-500)');
    return parts.length ? parts.join(', ') : null;
  }

  onCellValueChanged(col: EditableTableColumn<T>, value: any): void {
    this.cellValueChanged.emit({ col, value });
  }
}
